import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePointField } from "@/field/usePointField";
import { createHomeField } from "@/field/homeField";
import { prefersReducedMotion } from "@/field/pointField";
import { applyRouteMode, toggleMode } from "@/styles/theme";
import { resultsHref } from "@/navigation/trail";
import { ARCHIVE_TOTAL } from "@/data/catalogue";
import { SearchIcon } from "@/assets/icons/SearchIcon";
import { ArrowIcon } from "@/assets/icons/ArrowIcon";
import "./Home.css";

/**
 * The home screen.
 *
 * One job: take a query and hand it to the results screen. Everything else on
 * the page is the field, which exists to say what kind of thing the archive is
 * before the user has typed anything.
 *
 * The exit is worth the code it costs. The collapse ends on a flat wash in
 * exactly the results screen's ground colour, so the route change lands under
 * that wash instead of as a cut — the two screens read as one movement rather
 * than as two pages.
 */
export default function Home() {
  const navigate = useNavigate();

  const fieldRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const clearingRef = useRef<HTMLDivElement>(null);
  const liftRef = useRef<HTMLDivElement>(null);
  const modeRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);

  const [query, setQuery] = useState("");
  const [heroIn, setHeroIn] = useState(false);
  const [modeIn, setModeIn] = useState(false);

  // Read once: the value cannot change within a page view, and reading it inside
  // the memo below would make the field depend on a media query result.
  const reduced = useMemo(() => prefersReducedMotion(), []);

  // The query at the moment of launch. Read through a ref because the collapse
  // finishes on a later frame, by which time the closure that started it is stale.
  const pendingQuery = useRef("");

  useEffect(() => {
    applyRouteMode("light");
    document.body.dataset.scroll = "lock";
    return () => {
      delete document.body.dataset.scroll;
    };
  }, []);

  const goToResults = useCallback(() => {
    navigate(resultsHref({ trail: [], query: pendingQuery.current }));
  }, [navigate]);

  /**
   * Drive the hero, the clearing and the wash from the collapse.
   *
   * Written straight to `style` rather than through state: this runs on every
   * animation frame, and a `setState` per frame would re-render the page 60
   * times a second and tear down nothing useful in the process.
   */
  const onExitProgress = useCallback(
    ({ u }: { u: number; collapse: number }) => {
      const hero = heroRef.current;
      const lift = liftRef.current;
      const mode = modeRef.current;
      // Reversed entry: mode fades out first, then hero fades out + scales back to 0.96
      if (mode) {
        mode.style.opacity = String(
          Math.max(0, 0.5 * (1 - Math.min(1, u / 0.35))),
        );
      }
      if (hero) {
        const heroU = Math.max(0, Math.min(1, (u - 0.25) / 0.75));
        hero.style.opacity = String(Math.max(0, 1 - Math.pow(heroU, 1.2) / 1));
        hero.style.transform = `translate(-50%,-50%) scale(${(1 - 0.04 * Math.pow(heroU, 1.4)).toFixed(3)})`;
      }
      if (lift) {
        lift.style.opacity = String(
          Math.max(0, Math.min(1, (u - 0.56) / 0.44)),
        );
      }
    },
    [],
  );

  const field = useMemo(
    () =>
      createHomeField({
        onExitProgress,
        onExitComplete: goToResults,
        reducedMotion: reduced,
        getMouse: () => mouseRef.current,
      }),
    [onExitProgress, goToResults, reduced],
  );

  usePointField({
    containerRef: fieldRef,
    canvasRef,
    render: field.render,
    freezeOnHover: false,
  });

  /* The wordmark resolves after a beat of pure field. */
  useEffect(() => {
    if (reduced) {
      setHeroIn(true);
      setModeIn(true);
      return;
    }
    const a = window.setTimeout(() => setHeroIn(true), 1100);
    const b = window.setTimeout(() => setModeIn(true), 2800);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, [reduced]);

  const launch = useCallback(() => {
    if (field.running()) return;
    pendingQuery.current = query.trim();
    inputRef.current?.blur();
    field.launch();
  }, [field, query]);

  return (
    <div
      className="void"
      ref={fieldRef}
      onMouseMove={(e) => {
        const r = fieldRef.current?.getBoundingClientRect();
        if (r)
          mouseRef.current = { x: e.clientX - r.left, y: e.clientY - r.top };
      }}
      onMouseLeave={() => {
        mouseRef.current = null;
      }}
    >
      <canvas className="pf" ref={canvasRef} />
      <div className="clearing" ref={clearingRef} />

      <div className={`hero${heroIn ? " in" : ""}`} ref={heroRef}>
        <h1 className="mark">BVLGARI</h1>
        <div className="seek">
          <span className="mag" aria-hidden="true">
            <SearchIcon size={18} />
          </span>
          <input
            ref={inputRef}
            id="q"
            type="text"
            autoComplete="off"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                launch();
              }
            }}
            placeholder="descrivi il pezzo che cerchi&#8230;"
            aria-label="Cerca nell'archivio asset 3D"
          />
          <button
            className="go"
            type="button"
            onClick={launch}
            aria-label="Cerca"
          >
            <ArrowIcon size={16} />
          </button>
        </div>
        <div className="meta">
          {ARCHIVE_TOTAL.toLocaleString("it-IT")} asset &middot; rilievo
          continuo
        </div>
      </div>

      <button
        ref={modeRef}
        className={`home-mode${modeIn ? " in" : ""}`}
        type="button"
        onClick={() => toggleMode()}
        aria-label="Cambia tema"
      >
        &#9680;
      </button>

      <div className="lift" ref={liftRef} />
    </div>
  );
}
